import { useEffect } from 'react';
import { Control, Controller } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import {
  Select,
  DateInput,
  InputText,
  SelectSize,
  InputSize,
  DateInputSize,
  IconEdit,
  IconAlternativeSandWatch,
  IconChangeLoClock,
  Checkbox,
  Autocomplete,
  ButtonSecondary,
  IconExpand,
  ButtonSize,
  IconLoader,
  PermissionEnum,
} from '@ui/src';
import InfoRow from '../info-row';
import StepDocuments from './step-three-documents';
import {
  CRSAndFatcaFormValues,
  NewApplicationStatus,
  NewApplicationSumSubStatus,
  SumSubReviewStatus,
} from '../../../hoc/new-application/types';
import { IndividualApplicationStepThreeTypes } from '../types';
import StepPartBlock from '../corporate/parts/step-part-block';
import PepQuestionnarie from '../corporate/parts/pep-questionnarie';
import CRSAndFatca from './parts/crs-and-fatca';
import RegulatedCompanyDocuments from './parts/documents';
import {
  COFAndIndividualQuestionnarieFormValues,
  CorporateQuestionnarieFormValues,
} from '../../../hoc/customers/types';

import cn from '../new-application.module.css';

const NewApplicationInfoStepThree = ({
  sumSubReviewStatus,
  sumSubStatus,
  // phone,
  id,
  countries,
  control,
  documents,
  status,
  applicationId,
  // email,
  isEditable,
  sumSubDocuments,
  onGroupSelect,
  selectedDocumentsCounts,
  documentsVisible,
  setSelected,
  cof,
  search,
  errors,
  renameAvailable,
  changeLogsAvailable,
  sumSubId,
  regulatedCompanyScheme,
  fields,
  clearErrors,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  questionnarieFields,
  questionnarieSetValue,
  selectedCrsAndFatcaId,
  crsAndFatcaIsLoading,
  crsAndFatcaIsFetching,
  deleteCrsAndFatcaInProgress,
  onDeleteCRSAndFatca,
  crsAndFatcaValues,
  setCrsAndFatcaValues,
  setHaveSecondPassport,
  haveSecondPassport,
  setValue,
  allowedDocumentsCategories,
  activePermissions,
  cofRegulatedCompanyScheme,
  cofWithDocuments,
}: IndividualApplicationStepThreeTypes) => {
  useEffect(() => {
    if (
      questionnarieFields &&
      (questionnarieFields?.secondPassportExpiryDate ||
        questionnarieFields?.secondPassportIssueCountryId ||
        questionnarieFields?.secondPassportIssueDate ||
        questionnarieFields?.secondPassportNumber) &&
      !haveSecondPassport
    ) {
      setHaveSecondPassport(true);
    }
  }, [questionnarieFields, haveSecondPassport]);

  let sumSubReviewStatusText = 'In Progress';
  let sumSubStatusText = 'None';
  let sumSubStatusClassName = cn.sumSubYellow;

  if (sumSubReviewStatus === SumSubReviewStatus.COMPLETED) {
    sumSubReviewStatusText = 'Completed';
  } else if (sumSubReviewStatus === SumSubReviewStatus.PENDING) {
    sumSubReviewStatusText = 'Pending';
  }

  if (sumSubStatus === NewApplicationSumSubStatus.GREEN) {
    sumSubStatusText = 'Green';
    sumSubStatusClassName = cn.sumSubGreen;
  } else if (sumSubStatus === NewApplicationSumSubStatus.RED) {
    sumSubStatusText = 'Red';
    sumSubStatusClassName = cn.sumSubRed;
  }

  const IDDocument = sumSubDocuments && sumSubDocuments[0];

  return (
    <div className={cn.stepBlockWrapper} style={{ zIndex: 18 }}>
      <div className={cn.stepBlockContent}>
        <StepPartBlock title="CDD Form" zIndex={14}>
          <div className={cn.newAppliationStepThreeControls}>
            {id && (
              <NavLink
                to={`https://cockpit.sumsub.com/checkus#/applicant/${id}/basicInfo`}
                className={`${cn.newApplicationCopyLink} ${cn.newApplicationExternalLink}`}
                target="_blank"
                tabIndex={-1}
              >
                <IconEdit />
                Go to SumSub admin
              </NavLink>
            )}
            {changeLogsAvailable && sumSubId && (
              <NavLink
                to={`/administration/change-logs?entityType=SUM_SUB_APPLICATION&entityId=${sumSubId}`}
                className={`${cn.newApplicationCopyLink} ${cn.newApplicationExternalLink} ${cn.changeLogsLink}`}
                target="_blank"
                tabIndex={-1}
              >
                <IconChangeLoClock width={20} height={20} />
                Change Logs
              </NavLink>
            )}
            {!id && (
              <div className={cn.emptySumSub}>
                <div className={cn.iconWrapper}>
                  <IconAlternativeSandWatch />
                </div>
                <div className={cn.textWrapper}>
                  <div className={cn.generalText}>
                    IDV via Sumsub has not been completed yet
                  </div>
                  <div className={cn.additionalText}>
                    The application is created automatically when the customer
                    goes to the step of sumsub application in the Internet Bank.
                  </div>
                </div>
              </div>
            )}
          </div>
          {errors?.sumSubApplication?.message && (
            <div className={cn.sumsubError}>
              {errors?.sumSubApplication?.message}
            </div>
          )}
          {id && (
            <div className={cn.subSubInfoWrapper}>
              <div
                className={`${cn.stepThreeRow} ${cn.cddRow}`}
                id="sumSubApplicationId"
              >
                SumSub ID:
                <span>{id}</span>
              </div>
              <div
                className={`${cn.stepThreeRow} ${cn.cddRow}`}
                id="sumSubStatus"
              >
                Status:
                <span className={sumSubStatusClassName}>
                  {`${sumSubReviewStatusText} (${sumSubStatusText})`}
                </span>
              </div>
            </div>
          )}
        </StepPartBlock>
        <StepPartBlock title="General information" zIndex={13}>
          <div className={cn.cddBlockTitle}>
            Full permanent residential address
          </div>
          <div className={cn.cddBlockContentWrapper}>
            <div className={cn.cddBlockContent}>
              <InfoRow title="Country" id="country" lessPadding>
                <Controller
                  control={control}
                  name="countryId"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="countryId"
                      placeholder="-"
                      list={countries || []}
                      value={value}
                      size={SelectSize.SMALL}
                      selectWrapperClassName={cn.selectDisabled}
                      onChange={onChange}
                      tabIndex={-1}
                      disabled
                      borderless
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow title="City" id="city" lessPadding>
                <Controller
                  control={control}
                  name="city"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="city"
                      placeholder="-"
                      value={value}
                      onChange={onChange}
                      size={InputSize.SMALL}
                      className="borderless"
                      tabIndex={-1}
                      disabled
                    />
                  )}
                />
              </InfoRow>
              <InfoRow title="Postal code" id="postalCode" lessPadding>
                <Controller
                  control={control}
                  name="postalCode"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="postalCode"
                      placeholder="-"
                      value={value}
                      onChange={onChange}
                      size={InputSize.SMALL}
                      className="borderless"
                      tabIndex={-1}
                      disabled
                    />
                  )}
                />
              </InfoRow>
              <InfoRow title="Address line 1" id="address1" lessPadding>
                <Controller
                  control={control}
                  name="address1"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="address1"
                      placeholder="-"
                      value={value}
                      onChange={onChange}
                      size={InputSize.SMALL}
                      className="borderless"
                      tabIndex={-1}
                      disabled
                    />
                  )}
                />
              </InfoRow>
              <InfoRow title="Address line 2" id="address2" lessPadding>
                <Controller
                  control={control}
                  name="address2"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="address"
                      placeholder="-"
                      value={value}
                      onChange={onChange}
                      size={InputSize.SMALL}
                      className="borderless"
                      tabIndex={-1}
                      disabled
                    />
                  )}
                />
              </InfoRow>
            </div>
          </div>
          {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
            <>
              <div className={cn.cddBlockTitle}>Correspondence address</div>
              <div className={cn.cddBlockContentWrapper}>
                <div className={cn.cddBlockContent}>
                  <div
                    className={`${cn.addressCheckboxWrapper} ${cn.stepOneCheckboxWrapper} ${cn.cddBlockContentCheckbox}`}
                  >
                    <Controller
                      control={control}
                      name="correspondenceAddressSameAsRegisteredAddress"
                      defaultValue={false}
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          name="correspondenceAddressSameAsRegisteredAddress"
                          type="checkbox"
                          checked={value}
                          onChange={(e) => {
                            onChange(e.target.checked);

                            if (e.target.checked) {
                              setValue('correspondenceAddress1', '');
                              setValue('correspondenceAddress2', '');
                              setValue('correspondenceCity', '');
                              setValue('correspondenceCountryId', '');
                              setValue('correspondencePostalCode', '');
                            }
                          }}
                          title="Coincides with the registered address"
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          labelClassName={cn.addressCheckbox}
                        />
                      )}
                    />
                  </div>
                  {!fields?.correspondenceAddressSameAsRegisteredAddress && (
                    <>
                      <InfoRow
                        title="Country"
                        id="correspondenceCountryId"
                        lessPadding
                      >
                        <Controller
                          control={control}
                          name="correspondenceCountryId"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Autocomplete
                              name="correspondenceCountryId"
                              placeholder="Select country..."
                              list={countries}
                              value={value}
                              size={SelectSize.SMALL}
                              className={`${cn.newApplicationAutocomplete} ${
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED
                                  ? cn.newApplicationAutocompleteDisabled
                                  : ''
                              }`}
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              onChange={(e) => {
                                onChange(e);
                                clearErrors(['correspondenceCountryId']);
                              }}
                              error={errors?.correspondenceCountryId?.message}
                              borderless
                              fullWidth
                            />
                          )}
                        />
                      </InfoRow>
                      <InfoRow title="City" id="correspondenceCity" lessPadding>
                        <Controller
                          control={control}
                          name="correspondenceCity"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <InputText
                              name="correspondenceCity"
                              placeholder="Enter city..."
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                clearErrors(['correspondenceCity']);
                              }}
                              size={InputSize.SMALL}
                              className="borderless"
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              error={errors?.correspondenceCity?.message}
                            />
                          )}
                        />
                      </InfoRow>
                      <InfoRow
                        title="Postal code"
                        id="correspondencePostalCode"
                        lessPadding
                      >
                        <Controller
                          control={control}
                          name="correspondencePostalCode"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <InputText
                              name="correspondencePostalCode"
                              placeholder="Enter postal code..."
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                clearErrors(['correspondencePostalCode']);
                              }}
                              size={InputSize.SMALL}
                              className="borderless"
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              error={errors?.correspondencePostalCode?.message}
                            />
                          )}
                        />
                      </InfoRow>
                      <InfoRow
                        title="Address 1"
                        id="correspondenceAddress1"
                        lessPadding
                      >
                        <Controller
                          control={control}
                          name="correspondenceAddress1"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <InputText
                              name="correspondenceAddress1"
                              placeholder="Enter address 1..."
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                clearErrors(['correspondenceAddress1']);
                              }}
                              size={InputSize.SMALL}
                              className="borderless"
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              error={errors?.correspondenceAddress1?.message}
                            />
                          )}
                        />
                      </InfoRow>
                      <InfoRow
                        title="Address 2"
                        id="correspondenceAddress2"
                        lessPadding
                      >
                        <Controller
                          control={control}
                          name="correspondenceAddress2"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <InputText
                              name="correspondenceAddress2"
                              placeholder="Enter address 2..."
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                clearErrors(['correspondenceAddress2']);
                              }}
                              size={InputSize.SMALL}
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              className="borderless"
                              error={errors?.correspondenceAddress2?.message}
                            />
                          )}
                        />
                      </InfoRow>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          <div className={cn.cddBlockTitle}>Passport</div>
          <div className={cn.cddBlockContentWrapper}>
            <div className={cn.cddBlockContent}>
              <InfoRow title="ID Type" lessPadding>
                <InputText
                  name="idType"
                  placeholder="-"
                  value={IDDocument?.type}
                  size={InputSize.SMALL}
                  className="borderless"
                  tabIndex={-1}
                  disabled
                />
              </InfoRow>
              <InfoRow
                title="ID / Passport number"
                id="passportNumber"
                lessPadding
              >
                <InputText
                  name="passportNumber"
                  placeholder="-"
                  value={IDDocument?.number}
                  size={InputSize.SMALL}
                  className="borderless"
                  tabIndex={-1}
                  disabled
                />
              </InfoRow>
              <InfoRow
                title="ID / Passport expired date"
                id="passportExpiredDate"
                lessPadding
              >
                <DateInput
                  value={IDDocument?.expDate}
                  placeholder="-"
                  size={DateInputSize.SMALL}
                  className={`${cn.dateInputDisabled} ${
                    IDDocument?.expDate ? cn.dateInputDisabledWithValue : ''
                  }`}
                  tabIndex={-1}
                  borderless
                />
              </InfoRow>
            </div>
          </div>
          {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
            <>
              <div className={cn.cddBlockTitle}>2nd Passport</div>
              <div className={cn.cddBlockContentWrapper}>
                <div className={cn.cddBlockContent}>
                  <div
                    className={`${cn.addressCheckboxWrapper} ${cn.stepOneCheckboxWrapper} ${cn.cddBlockContentCheckbox}`}
                  >
                    <Checkbox
                      name="haveSecondPassport"
                      type="checkbox"
                      checked={haveSecondPassport}
                      onChange={(e) => setHaveSecondPassport(e.target.checked)}
                      title="Having a second passport"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        ) ||
                        Boolean(
                          questionnarieFields?.otherNationalitiesIds &&
                          (questionnarieErrors?.secondPassportIssueCountryId ||
                            questionnarieErrors?.secondPassportIssueDate ||
                            questionnarieErrors?.secondPassportNumber),
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      labelClassName={cn.addressCheckbox}
                    />
                  </div>
                  {haveSecondPassport && (
                    <>
                      <InfoRow
                        title="ID / Passport number"
                        id="secondPassportNumber"
                        lessPadding
                      >
                        <Controller
                          control={questionnarieControl}
                          name="secondPassportNumber"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <InputText
                              name="secondPassportNumber"
                              placeholder="Enter first name..."
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                questionnarieClearErrors([
                                  'secondPassportNumber',
                                ]);
                              }}
                              size={InputSize.SMALL}
                              className="borderless"
                              error={
                                questionnarieErrors?.secondPassportNumber
                                  ?.message
                              }
                              tabIndex={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                            />
                          )}
                        />
                      </InfoRow>
                      <InfoRow
                        title="ID / Passport issue country"
                        id="secondPassportIssueCountryId"
                        lessPadding
                      >
                        <Controller
                          control={questionnarieControl}
                          name="secondPassportIssueCountryId"
                          defaultValue=""
                          render={({ field: { onChange, value, name } }) => (
                            <Autocomplete
                              name={name}
                              placeholder="Select country..."
                              list={countries}
                              value={value}
                              size={SelectSize.SMALL}
                              onChange={(e) => {
                                onChange(e);
                                questionnarieClearErrors([
                                  'secondPassportIssueCountryId',
                                ]);
                              }}
                              selectWrapperClassName={
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED
                                  ? cn.selectDisabled
                                  : ''
                              }
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              tabIndex={
                                !isEditable &&
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              error={
                                questionnarieErrors
                                  ?.secondPassportIssueCountryId?.message
                              }
                              fullWidth
                              borderless
                            />
                          )}
                        />
                      </InfoRow>
                      <InfoRow
                        title="ID / Passport issue date"
                        id="secondPassportIssueDate"
                        lessPadding
                      >
                        <Controller
                          control={questionnarieControl}
                          name="secondPassportIssueDate"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => {
                            let className;

                            if (
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                            ) {
                              className = cn.dateInputDisabled;

                              if (value) {
                                className = `${cn.dateInputDisabled} ${
                                  value ? cn.dateInputDisabledWithValue : ''
                                }`;
                              }
                            }
                            return (
                              <DateInput
                                value={value || ''}
                                placeholder="Enter date..."
                                onChange={(date) => {
                                  onChange(date.toISOString());
                                  questionnarieClearErrors([
                                    'secondPassportIssueDate',
                                  ]);
                                }}
                                disabledAfter={new Date()}
                                className={className}
                                size={DateInputSize.SMALL}
                                error={
                                  questionnarieErrors?.secondPassportIssueDate
                                    ?.message
                                }
                                tabIndex={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                borderless
                                withoutCustomSelector
                              />
                            );
                          }}
                        />
                      </InfoRow>
                      <InfoRow
                        title="ID / Passport expiry date"
                        id="secondPassportExpiryDate"
                        lessPadding
                      >
                        <Controller
                          control={questionnarieControl}
                          name="secondPassportExpiryDate"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => {
                            let className;

                            if (
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                            ) {
                              className = cn.dateInputDisabled;

                              if (value) {
                                className = `${cn.dateInputDisabled} ${
                                  value ? cn.dateInputDisabledWithValue : ''
                                }`;
                              }
                            }
                            return (
                              <DateInput
                                value={value || ''}
                                placeholder="Enter date..."
                                onChange={(date) => {
                                  onChange(date.toISOString());
                                  questionnarieClearErrors([
                                    'secondPassportExpiryDate',
                                  ]);
                                }}
                                className={className}
                                size={DateInputSize.SMALL}
                                error={
                                  questionnarieErrors?.secondPassportExpiryDate
                                    ?.message
                                }
                                tabIndex={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                    ? -1
                                    : 0
                                }
                                disabled={
                                  !isEditable ||
                                  Boolean(
                                    status === NewApplicationStatus.ACCEPTED ||
                                    status === NewApplicationStatus.DECLINED,
                                  )
                                }
                                borderless
                                withoutCustomSelector
                              />
                            );
                          }}
                        />
                      </InfoRow>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {!regulatedCompanyScheme && !cofRegulatedCompanyScheme && (
            <>
              <div className={cn.cddBlockTitle}>
                US Citizen & Participation in politics
              </div>

              <div className={cn.cddBlockContentWrapper}>
                <div className={cn.cddBlockContent}>
                  <InfoRow
                    title="Participation in politics"
                    id="participatesInPolitics"
                    lessPadding
                  >
                    <Controller
                      control={control}
                      name="participatesInPolitics"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <Select
                          name="participatesInPolitics"
                          placeholder="Select an option"
                          list={[
                            { name: 'Yes, I am participating', value: 'yes' },
                            { name: 'Not participating', value: 'no' },
                          ]}
                          value={value as string}
                          size={SelectSize.SMALL}
                          selectWrapperClassName={
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED
                              ? cn.selectDisabled
                              : ''
                          }
                          onChange={onChange}
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          borderless
                        />
                      )}
                    />
                  </InfoRow>
                  <InfoRow title="US Citizen" id="isUSResident" lessPadding>
                    <Controller
                      control={control}
                      name="isUSResident"
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <Select
                          name="isUSResident"
                          placeholder="Select an option"
                          list={[
                            { name: 'Yes', value: 'yes' },
                            { name: 'No', value: 'no' },
                          ]}
                          value={value as string}
                          selectWrapperClassName={
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED
                              ? cn.selectDisabled
                              : ''
                          }
                          size={SelectSize.SMALL}
                          onChange={onChange}
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          borderless
                        />
                      )}
                    />
                  </InfoRow>
                </div>
              </div>
            </>
          )}
        </StepPartBlock>
        {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
          <>
            <PepQuestionnarie
              questionnarieControl={
                questionnarieControl as Control<
                  | COFAndIndividualQuestionnarieFormValues
                  | CorporateQuestionnarieFormValues
                >
              }
              questionnarieClearErrors={questionnarieClearErrors}
              status={status}
              isEditable={isEditable}
              questionnarieErrors={questionnarieErrors}
              questionnarieFields={questionnarieFields}
              countries={countries}
              questionnarieSetValue={questionnarieSetValue}
              isIndividual
            />
            <StepPartBlock title="CRS and FATCA" zIndex={11}>
              <div className={cn.cddBlockTitle}>Tax residence</div>
              <div className={cn.cddBlockContentWrapper}>
                {questionnarieErrors?.questionnairesCrsFatcaDeclarations
                  ?.message && (
                  <div className={cn.questionnarieError}>
                    {
                      questionnarieErrors?.questionnairesCrsFatcaDeclarations
                        ?.message
                    }
                  </div>
                )}
                {(crsAndFatcaIsLoading || crsAndFatcaIsFetching) && (
                  <div className={cn.accountsCurrenciesLoader}>
                    <IconLoader />
                  </div>
                )}
                {!crsAndFatcaIsLoading && !crsAndFatcaIsFetching && (
                  <>
                    {!crsAndFatcaValues?.length && (
                      <div className={cn.emptyAccountsCurrenciesList}>
                        No tax residence added yet
                      </div>
                    )}
                    {crsAndFatcaValues?.map((item, index) => (
                      <CRSAndFatca
                        key={item.id}
                        crsAndFatca={item}
                        setCrsAndFatcaValues={setCrsAndFatcaValues}
                        countries={countries}
                        index={index}
                        isEditable={isEditable}
                        status={status}
                        crsAndFatcaValues={crsAndFatcaValues}
                        onDeleteCRSAndFatca={onDeleteCRSAndFatca}
                        selectedCrsAndFatcaId={selectedCrsAndFatcaId}
                        deleteCrsAndFatcaInProgress={
                          deleteCrsAndFatcaInProgress
                        }
                      />
                    ))}
                    <ButtonSecondary
                      text="Add tax residence"
                      icon={<IconExpand />}
                      className={`button-white ${cn.addAccountCurrencyButton}`}
                      size={ButtonSize.SMALL}
                      onClick={() => {
                        let nextCrsAndFatca: CRSAndFatcaFormValues[] = [];

                        if (crsAndFatcaValues) {
                          nextCrsAndFatca = (
                            [] as CRSAndFatcaFormValues[]
                          ).concat(crsAndFatcaValues);
                        }

                        nextCrsAndFatca.push({
                          id: `crs-and-fatca-${uuid()}`,
                          residenceCountryId: '',
                          taxIdentificationNumber: '',
                          noTinReason: '',
                        });

                        setCrsAndFatcaValues(nextCrsAndFatca);
                      }}
                    />
                  </>
                )}
              </div>
              {!cofRegulatedCompanyScheme && (
                <>
                  <div className={cn.cddBlockTitle}>US citizen</div>
                  <div className={cn.cddBlockContentWrapper}>
                    <div className={cn.cddBlockContent}>
                      <InfoRow
                        title="Are you a citizen of U.S.?"
                        id="isUSCitizen"
                        lessPadding
                      >
                        <Controller
                          control={questionnarieControl}
                          name="isUSCitizen"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Select
                              name="isUSCitizen"
                              placeholder="Select an option"
                              list={[
                                { name: 'Yes', value: 'yes' },
                                { name: 'No', value: 'no' },
                              ]}
                              value={value as string}
                              selectWrapperClassName={
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED
                                  ? cn.selectDisabled
                                  : ''
                              }
                              size={SelectSize.SMALL}
                              onChange={(e) => {
                                onChange(e);
                                questionnarieClearErrors('isUSCitizen');
                              }}
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              tabIndex={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              error={questionnarieErrors?.isUSCitizen?.message}
                              fullWidth
                              borderless
                            />
                          )}
                        />
                      </InfoRow>
                      <InfoRow
                        title="Are you a resident of U.S.?"
                        id="isUSResident"
                        lessPadding
                      >
                        <Controller
                          control={control}
                          name="isUSResident"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <Select
                              name="isUSResident"
                              placeholder="Select an option"
                              list={[
                                { name: 'Yes', value: 'yes' },
                                { name: 'No', value: 'no' },
                              ]}
                              value={value as string}
                              selectWrapperClassName={
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED
                                  ? cn.selectDisabled
                                  : ''
                              }
                              size={SelectSize.SMALL}
                              onChange={(e) => {
                                onChange(e);
                                clearErrors('isUSResident');
                              }}
                              disabled={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                              }
                              tabIndex={
                                !isEditable ||
                                Boolean(
                                  status === NewApplicationStatus.ACCEPTED ||
                                  status === NewApplicationStatus.DECLINED,
                                )
                                  ? -1
                                  : 0
                              }
                              error={errors?.isUSResident?.message}
                              fullWidth
                              borderless
                            />
                          )}
                        />
                      </InfoRow>
                    </div>
                  </div>
                </>
              )}
            </StepPartBlock>
          </>
        )}
        {documentsVisible &&
          !regulatedCompanyScheme &&
          !cofRegulatedCompanyScheme &&
          !cofWithDocuments && (
            <StepDocuments
              documents={documents}
              onGroupSelect={onGroupSelect}
              selectedDocumentsCounts={selectedDocumentsCounts}
              applicationId={applicationId}
              setSelected={setSelected}
              cof={cof}
              search={search}
              status={status}
              renameAvailable={renameAvailable}
            />
          )}
        {(regulatedCompanyScheme ||
          cofRegulatedCompanyScheme ||
          cofWithDocuments) && (
          <RegulatedCompanyDocuments
            documents={documents}
            applicationId={applicationId}
            applicationStatus={status}
            search={search}
            allowedDocumentsCategories={allowedDocumentsCategories}
            errors={errors}
            clearErrors={clearErrors}
            renameAvailable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
            )}
            isCof={cof}
          />
        )}
      </div>
    </div>
  );
};

export default NewApplicationInfoStepThree;
